<template>
    <div class="relative">
        <MoveableMarking
            v-if="enableMoveableMarking"
            :marking="moveableMarking"
            @update:marking="$emit('update:moveable-marking', $event)"
            @click-cta="$emit('click-moveable-marking-cta')"
        />

        <div
            :id="containerId"
            ref="container"
            v-resize="onResize"
            class="sticky"
            :style="{ height: renderHeight, top: `${offsetTop}px` }"
        ></div>

        <div :style="{ height: `calc(${viewerHeight}px - ${renderHeight})` }"></div>
    </div>
</template>

<script lang="ts">
import { computed, defineComponent, inject } from 'vue';
import type { PropType } from 'vue';
import type { Instance, CustomOverlayItem } from 'pspdfkit';
import { OverlayScrollbars } from 'overlayscrollbars';
import OverlayScrollbarsCss from 'overlayscrollbars/overlayscrollbars.css?inline';
import { useUIStore } from '@/stores/ui';
import type { Point2D } from '@/types/objects';
import { syncScroll } from '@/utils/syncScrollUtils';
import {
    type Marking,
    type InitialMarking,
    providePSPDFKit,
    providePdfViewerKey,
    provideTransformScreenToPageSpaceKey,
} from './PdfViewer';
import PdfViewerCss from './PdfViewer.scss?inline';
import MoveableMarking from './MoveableMarking.vue';

/**
 * Component to render pdf document.
 * The viewer is rendered sticky on the page, so specifying an offset is required.
 *
 * The viewer itself supports zooming and scrolling out of the box, but requires a fixed height.
 * In order to allow no only scrolling inside the viewer, but also on the page itself, we needed to have it stuck on the page and utilize a spacer to mimic the viewer height.
 * If we would set the viewer height to its pdf height and only rely on page scrolling, the build-in zoom, scrolling and page.press events would not work properly anymore and we need to customize it a lot.
 *
 * In order to allow scrolling the page and viewer, the scroll distance of each are synced.
 * The viewer is sticky as long as it can be scrolled.
 */
export default defineComponent({
    name: 'PdfViewer',
    components: {
        MoveableMarking,
    },
    provide() {
        return {
            [providePdfViewerKey as symbol]: computed(() => this.instance as Instance),
            /**
             * Provides a function that transforms screen points to page points.
             * This is needed as PSPDFKit is expecting the viewer is not moving on the page/screen,
             * but on our doc page it will actually change its position during scroll.
             *
             * By keeping track of the initial screen offset (when the viewer is setting/updating its position),
             * we can compute the offset and account for any change in viewer position.
             */
            [provideTransformScreenToPageSpaceKey as symbol]: (point: Point2D, pageIndex: number) => {
                const currOffset = this.getCurrentScreenOffset();

                return this.instance!.transformContentClientToPageSpace(
                    new this.PSPDFKit.Geometry.Point({
                        x: point.x - (currOffset.x - this.screenOffset.x),
                        y: point.y - (currOffset.y - this.screenOffset.y),
                    }),
                    pageIndex,
                ) as Point2D;
            },
        };
    },
    props: {
        /**
         * URL of the document to load in the viewer
         */
        src: {
            type: String,
            required: true,
        },
        /**
         * The offset of the viewer from the top of the page.
         * As its rendered sticky, the offset is required.
         */
        offsetTop: {
            type: Number,
            required: true,
        },
        /**
         * Current page of the document displayed
         */
        currentPage: {
            type: Number,
            default: 1,
        },
        /**
         * doc markings as stored in our database
         */
        markings: {
            type: Array as PropType<Marking[]>,
            default: () => [],
        },

        /**
         * selected marking
         */
        selectedMarking: {
            type: Object as PropType<Marking>,
            default: () => null,
        },
        /**
         * whether the moveable marking should be enabled
         */
        enableMoveableMarking: {
            type: Boolean,
            default: false,
        },
        /**
         * The moveable marking currently displayed
         */
        moveableMarking: {
            type: Object as PropType<InitialMarking | null>,
            default: null,
        },
    },
    emits: [
        'loaded',
        'update:current-page',
        'update:selected-marking',
        'update:moveable-marking',
        'click-moveable-marking-cta',
        'request-new-url',
        'set-viewer-total-pages',
    ],
    setup() {
        const PSPDFKit = inject(providePSPDFKit);
        if (!PSPDFKit) throw new Error('PSPDFKit is not provided');

        return {
            PSPDFKit,
            locale: useUIStore().locale,
            pageMap: new Map<number, { width: number; height: number }>(),
        };
    },
    data() {
        return {
            // See `provideTransformScreenToPageSpaceKey`
            screenOffset: { x: 0, y: 0 } as Point2D,
            viewerHeight: 0 as number,
            instance: null as Instance | null,
            totalPageCount: 0,
            currentRotation: 0,
            markingElements: [] as CustomOverlayItem[],
            viewerScrollBox: null as HTMLElement | null,
            viewerScrollbars: null as OverlayScrollbars | null,
            scrollSync: null as null | ReturnType<typeof syncScroll>,
            viewerVisibleObserver: null as IntersectionObserver | null,
            highlightedElement: null as HTMLElement | null,
            loadRetried: false,
        };
    },
    computed: {
        containerId() {
            return `pdf-container-${this.$id}`;
        },
        renderHeight(): string {
            return `calc(100dvh - ${this.offsetTop}px)`;
        },
    },
    watch: {
        /**
         * Triggers unloading and loading when there's a new document to load.
         */
        src(val) {
            if (val) {
                this.loadPSPDFKit();
            }
        },
        currentPage(page) {
            this.instance?.setViewState(this.instance.viewState.set('currentPageIndex', page - 1));
        },
        'markings.length': {
            handler(newLength) {
                if (newLength > 0) {
                    this.displayMarkings(this.markings);
                } else {
                    this.hideAllMarkings();
                }
            },
        },
        offsetTop() {
            this.unobserveViewerVisibility();
            this.observeViewerVisibility();
        },
        locale(locale) {
            this.instance?.setLocale(locale);
        },
        selectedMarking(newMarking) {
            if (newMarking) {
                this.scrollToMarking(newMarking);
            } else {
                this.clearHighlights();
            }
        },
        viewerHeight() {
            // We need to sync the scroll positions after the next frame was painted
            window.requestAnimationFrame(() => {
                this.scrollSync?.resync.elem2();
                this.scrollSync?.resync.elem1();
            });
        },
    },
    mounted() {
        /**
         * We wait until the template has been rendered to load the document into the library.
         */
        this.loadPSPDFKit();
    },
    beforeUnmount() {
        this.reset();
    },
    methods: {
        async loadPSPDFKit(): Promise<Instance | null> {
            this.reset();
            try {
                const instance = await this.PSPDFKit.load({
                    licenseKey: import.meta.env.MIX_NUTRIENT_WEB_SDK_KEY,
                    baseUrl: `${new URL(import.meta.url).origin}/assets/`,
                    document: this.src,
                    container: `#${this.containerId}`,
                    locale: this.locale,
                    styleSheets: [
                        `data:text/css;base64,${btoa(OverlayScrollbarsCss)}`,
                        `data:text/css;base64,${btoa(PdfViewerCss)}`,
                    ],
                    initialViewState: new this.PSPDFKit.ViewState({
                        allowExport: false,
                        allowPrinting: false,
                        showToolbar: false,
                        layoutMode: 'SINGLE',
                        scrollMode: 'CONTINUOUS',
                    }),
                });
                this.instance = this.disableExportAndPrintFeatures(instance);

                this.$emit('loaded');
                this.totalPageCount = this.instance?.totalPageCount ?? 0;
                this.$emit('set-viewer-total-pages', this.totalPageCount);

                this.instance.addEventListener('viewState.zoom.change', this.updateViewerHeight);
                this.instance.addEventListener('viewState.currentPageIndex.change', this.updateCurrentPage);

                this.updatePageInfo();

                this.initScrollbox(this.instance);
                this.observeViewerVisibility();
                this.updateViewerHeight();
                // we need to save/update the current screen offset of the viewer to account for movements of the viewer later
                // See: `provideTransformScreenToPageSpaceKey`
                this.updateScreenOffset();

                if (this.markings.length > 0) {
                    this.displayMarkings(this.markings);
                }

                return this.instance;
            } catch (error) {
                if (this.loadRetried) {
                    throw error ?? new Error('Document cannot be loaded.');
                }
                this.reloadPdf();
                return null;
            }
        },
        disableExportAndPrintFeatures(instance: Instance) {
            /* eslint-disable no-param-reassign */
            instance.print = () => {};
            instance.exportOffice = () => Promise.resolve(new ArrayBuffer(0));
            instance.exportPDF = () => Promise.resolve(new ArrayBuffer(0));
            instance.exportPDFWithOperations = () => Promise.resolve(new ArrayBuffer(0));
            instance.renderPageAsArrayBuffer = () => Promise.resolve(new ArrayBuffer(0));
            instance.renderPageAsImageURL = () => Promise.resolve('');
            /* eslint-enable no-param-reassign */
            return instance;
        },
        async reloadPdf() {
            this.loadRetried = true;
            this.reset();
            this.$emit('request-new-url');
        },
        async initScrollbox(instance: Instance) {
            this.viewerScrollBox = instance.contentDocument.querySelector('.PSPDFKit-Scroll') as HTMLElement;
            this.viewerScrollbars = OverlayScrollbars(
                {
                    target: this.viewerScrollBox,
                    elements: {
                        viewport: this.viewerScrollBox,
                        host: this.viewerScrollBox.parentElement,
                    },
                    scrollbars: {
                        slot: this.viewerScrollBox.parentElement,
                    },
                },
                {
                    overflow: {
                        y: 'hidden',
                    },
                    scrollbars: {
                        autoHide: 'leave',
                    },
                },
            );
            await this.$nextTick();
            this.syncScrolling();
        },
        destroyScrollbox() {
            this.scrollSync?.unsync();
            this.viewerScrollbars?.destroy();
            this.viewerScrollBox = null;
            this.viewerScrollbars = null;
        },
        /**
         * syncs scrolls on the viewer with the window and vice-versa
         */
        syncScrolling() {
            this.scrollSync = syncScroll(
                {
                    el: this.viewerScrollBox!,
                    distance: () => ({
                        // we want to scroll the viewer by the distance it is sticky relative to its offsetParent
                        top: (this.$refs.container as HTMLElement).offsetTop,
                    }),
                },
                {
                    el: window,
                    distance: () => ({
                        top:
                            window.scrollY +
                            this.viewerScrollBox!.scrollTop -
                            (this.$refs.container as HTMLElement).offsetTop,
                    }),
                },
            );
        },
        /**
         * This function will observe whether the viewer is fully visible and invoke `onViewerFullyVisible` accordingly.
         */
        observeViewerVisibility() {
            this.viewerVisibleObserver = new IntersectionObserver(
                (entries) => this.onViewerFullyVisible(entries[0].intersectionRatio === 1),
                {
                    threshold: [0, 1],
                    root: document,
                    rootMargin: `-${this.offsetTop}px 0px 0px 0px`,
                },
            );
            this.viewerVisibleObserver.observe(this.$refs.container as HTMLElement);
        },
        unobserveViewerVisibility() {
            if (!this.viewerVisibleObserver) return;

            this.viewerVisibleObserver.disconnect();
            this.onViewerFullyVisible(false);
        },
        /**
         * It will make the viewer scrollable only when it's fully visible.
         * This allows scrolling to the viewer if its not fully visible.
         */
        onViewerFullyVisible(isVisible: boolean) {
            this.viewerScrollbars?.options({
                overflow: { y: isVisible ? 'scroll' : 'hidden' },
            });
        },
        onResize() {
            // trigger a resize event on the viewer instance
            this.instance?.contentWindow.dispatchEvent(new Event('resize'));

            this.updateViewerHeight();
            this.updateScreenOffset();
        },
        getCurrentScreenOffset(): Point2D {
            const rect = (this.$refs.container as HTMLElement).getBoundingClientRect();
            return { x: rect.x, y: rect.y };
        },
        updateScreenOffset() {
            this.screenOffset = this.getCurrentScreenOffset();
        },
        reset() {
            this.pageMap.clear();
            this.destroyScrollbox();
            this.unobserveViewerVisibility();

            /**
             * Clean up when the component is unmounted so it's ready to load another document.
             */
            this.PSPDFKit.unload(`#${this.containerId}`);
            this.viewerHeight = 0;

            if (this.instance) {
                this.instance.removeEventListener('viewState.zoom.change', this.updateViewerHeight);
                this.instance.removeEventListener('viewState.currentPageIndex.change', this.updateCurrentPage);
            }
        },
        zoomIn() {
            this.instance?.setViewState(this.instance.viewState.zoomIn());
        },
        zoomOut() {
            this.instance?.setViewState(this.instance.viewState.zoomOut());
        },
        rotate() {
            this.instance?.setViewState(this.instance.viewState.rotateRight());
            this.currentRotation = (this.currentRotation + 90) % 360;
            this.updateViewerHeight();
        },
        async search(term: string) {
            if (this.instance) {
                let results;
                // if the search term is empty, we need to clear the results
                if (!term) {
                    results = this.PSPDFKit.Immutable.List([]);
                } else {
                    if (!this.instance.searchState.results.size || term !== this.instance.searchState.term) {
                        const searchState = this.instance.searchState.set('term', term);
                        this.instance.setSearchState(searchState);
                        results = await this.instance.search(term);
                        const newState = this.instance.searchState
                            .set('results', results)
                            .set('focusedResultIndex', -1);
                        this.instance.setSearchState(newState);
                    }
                    this.showNextResult();
                }
            }
        },
        showNextResult() {
            this.instance?.setSearchState((state) => {
                const nextIndex = state.focusedResultIndex + 1 >= state.results.size ? 0 : state.focusedResultIndex + 1;
                const newState = state.set('focusedResultIndex', nextIndex);

                // Navigate to the page of the focused result
                const focusedResult = newState.results.get(nextIndex);
                if (focusedResult?.pageIndex != null) {
                    this.updateCurrentPage(focusedResult.pageIndex);
                }
                return newState;
            });
        },
        updatePageInfo() {
            if (!this.instance) return;
            this.pageMap.clear();

            for (let i = 0; i < this.instance.totalPageCount; i += 1) {
                const pageInfo = this.instance.pageInfoForIndex(i);
                if (pageInfo) {
                    this.pageMap.set(i + 1, {
                        height: pageInfo.height,
                        width: pageInfo.width,
                    });
                }
            }
        },

        updateViewerHeight() {
            if (!this.instance) return;

            let containerHeight = 0;
            const { spreadSpacing } = this.instance.viewState;
            const isRotated = this.currentRotation === 90 || this.currentRotation === 270;

            for (let i = 1; i <= this.instance.totalPageCount; i += 1) {
                const page = this.pageMap.get(i);
                if (page) {
                    // Use width as height and height as width when rotated 90 or 270 degrees
                    const pageHeight = isRotated ? page.width : page.height;
                    containerHeight += pageHeight + spreadSpacing;
                }
            }
            containerHeight *= this.instance.currentZoomLevel; // Convert to client dimension.
            containerHeight += this.instance.viewState.viewportPadding.vertical * 2; // Viewport padding.

            this.viewerHeight = Math.ceil(containerHeight);
        },
        updateCurrentPage(index: number) {
            this.$emit('update:current-page', index + 1);
        },

        /**
         * The markings are added as custom overlay items
         * (https://pspdfkit.com/guides/web/customizing-the-interface/creating-custom-overlay-items/)
         *
         * @param markings
         */
        displayMarkings(markings: Marking[]) {
            if (!this.instance || !markings.length) return;

            const markingIds = new Set(markings.map((marking) => marking.question_id.toString()));

            // Remove outdated overlay items
            this.markingElements = this.markingElements.filter((overlayItem) => {
                if (!markingIds.has(overlayItem.id)) {
                    this.instance?.removeCustomOverlayItem(overlayItem.id); // Remove from viewer
                    return false;
                }
                return true;
            });

            // Add new overlay items for markings that are not already displayed
            const existingIds = new Set(this.markingElements.map((el) => el.id));
            markings.forEach((marking) => {
                const markingId = marking.question_id.toString();
                if (!existingIds.has(markingId)) {
                    const overlayItem = this.createOverlayItemForMarking(marking);
                    this.markingElements.push(overlayItem);
                }
            });
            this.showAllMarkings();
        },
        highlightMarking(markingElement: HTMLElement) {
            this.clearHighlights();
            markingElement.classList.add('highlighted');
            this.highlightedElement = markingElement;
        },
        getScalingFactors(marking: Marking) {
            const page = this.pageMap.get(marking.page);
            if (!page) throw new Error('Page not found');

            return {
                widthFactor: page.width / marking.page_width,
                heightFactor: page.height / marking.page_height,
                page,
            };
        },
        createOverlayItemForMarking(m: Marking): CustomOverlayItem {
            const { widthFactor, heightFactor } = this.getScalingFactors(m);

            const adjustedHeight = m.height * heightFactor;
            const adjustedWidth = m.width * widthFactor;
            const adjustedYStart = m.ystart * heightFactor;
            const adjustedXStart = m.xstart * widthFactor;

            const markingEl = document.createElement('div');
            markingEl.className = 'marking';
            markingEl.style.width = `${adjustedWidth}px`;
            markingEl.style.height = `${adjustedHeight}px`;
            markingEl.style.display = 'block';
            markingEl.setAttribute('data-marking-id', m.question_id.toString());

            /* eslint-disable tipsi/remove-event-listener -- the event listeners are destroyed when the marking is removed */
            markingEl.addEventListener('click', () => {
                this.highlightMarking(markingEl);
                this.$emit('update:selected-marking', m);
            });

            // prevent triggering the pspdfkit page.press event
            markingEl.addEventListener('pointerup', (e) => {
                e.stopPropagation();
            });

            // prevent triggering the pspdfkit page.press event on touch devices
            markingEl.addEventListener('touchend', (e) => {
                e.stopPropagation();
            });
            /* eslint-enable tipsi/remove-event-listener */

            const item = new this.PSPDFKit.CustomOverlayItem({
                id: m.question_id.toString(),
                node: markingEl,
                pageIndex: m.page - 1,
                position: new this.PSPDFKit.Geometry.Point({ x: adjustedXStart, y: adjustedYStart }),
            });

            // If this is the currently selected marking, highlight it
            if (this.selectedMarking?.question_id === m.question_id) {
                this.highlightMarking(markingEl);
            }

            this.instance!.setCustomOverlayItem(item);
            return item as CustomOverlayItem;
        },
        showAllMarkings() {
            this.markingElements.forEach((el) => {
                (el.node as HTMLElement).style.display = 'block';
            });
        },
        hideAllMarkings() {
            this.markingElements.forEach((el) => {
                (el.node as HTMLElement).style.display = 'none';
            });
        },
        async scrollToMarking(marking: Marking) {
            if (!this.instance) return;
            const { widthFactor, heightFactor } = this.getScalingFactors(marking);

            const rect = new this.PSPDFKit.Geometry.Rect({
                left: marking.xstart * widthFactor,
                top: marking.ystart * heightFactor,
                width: marking.width * widthFactor,
                height: marking.height * heightFactor,
            });

            await this.instance.jumpToRect(marking.page - 1, rect);

            // Try to find existing marking element to highlight it,
            // if not found it will be highlighted when created via createOverlayItemForMarking
            const existingMarking = this.markingElements.find((item) => item.id === marking.question_id.toString());
            if (existingMarking) {
                this.highlightMarking(existingMarking.node as HTMLElement);
            }
        },
        clearHighlights() {
            this.highlightedElement?.classList.remove('highlighted');
            this.highlightedElement = null;
        },
    },
});
</script>
